/**
 * Codes monitor page (Wave 5, spec §2 item 10): allowlisted jurisdiction
 * sources (list + add — the server exposes no delete) with one global
 * "Check now" run (POST /api/scraper/run, job progress over SSE) and the
 * human review queue for detected changes.
 *
 * The human-review posture is front and center: istari never auto-updates
 * rules — the monitor only detects changes; a human acknowledges or
 * dismisses each one, with recorded notes.
 */
import { useEffect, useState } from "react";
import type { CSSProperties, FormEvent } from "react";
import type { ReviewItem, ReviewStatus, ScraperSource } from "../api/types";
import { useCodesMonitorStore } from "../state/codesMonitor";

const cardStyle: CSSProperties = {
  background: "#21213a",
  borderRadius: 8,
  padding: "0.8rem 0.9rem",
  marginBottom: "0.6rem",
};

const inputStyle: CSSProperties = {
  background: "#12121e",
  color: "#d8d8e8",
  border: "1px solid #44446a",
  borderRadius: 6,
  padding: "0.4rem 0.6rem",
};

const buttonStyle: CSSProperties = {
  padding: "0.4rem 0.85rem",
  borderRadius: 6,
  border: "1px solid #4a90d9",
  background: "#4a90d9",
  color: "#fff",
  cursor: "pointer",
  fontWeight: 600,
  fontSize: "0.82rem",
};

const ghostButton: CSSProperties = {
  ...buttonStyle,
  background: "transparent",
  color: "#4a90d9",
  fontWeight: 400,
};

const sectionTitle: CSSProperties = {
  margin: "1.2rem 0 0.5rem",
  fontSize: "0.8rem",
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  color: "#8e8eb8",
};

function lastChecked(ms: number | null): string {
  return ms === null ? "never" : new Date(ms).toLocaleString();
}

const STATUS_COLORS: Record<ReviewStatus, string> = {
  open: "#e5c07b",
  acknowledged: "#98c379",
  dismissed: "#8e8eb8",
};

function ReviewItemCard({ item, source }: { item: ReviewItem; source: ScraperSource | undefined }) {
  const resolveItem = useCodesMonitorStore((s) => s.resolveItem);
  const [notes, setNotes] = useState("");

  return (
    <li data-testid="review-item" style={cardStyle}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: "1rem" }}>
        <div>
          <strong>{source?.jurisdiction ?? item.source_id}</strong>{" "}
          {source !== undefined && (
            <a
              href={source.url}
              target="_blank"
              rel="noreferrer"
              style={{ color: "#4a90d9", fontSize: "0.82rem" }}
            >
              source page ↗
            </a>
          )}
          <div style={{ color: "#8e8eb8", fontSize: "0.8rem" }}>
            detected {lastChecked(item.detected_ms)}
          </div>
        </div>
        <span
          data-testid="review-status"
          style={{ color: STATUS_COLORS[item.status], fontSize: "0.82rem", fontWeight: 600 }}
        >
          {item.status}
        </span>
      </div>

      <p data-testid="review-summary" style={{ margin: "0.5rem 0", fontSize: "0.88rem" }}>
        {item.diff_summary}
      </p>

      {item.status === "open" ? (
        <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", flexWrap: "wrap" }}>
          <input
            data-testid="review-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Reviewer notes (recorded with the decision)"
            style={{ ...inputStyle, flex: 1, minWidth: 220 }}
          />
          <button
            type="button"
            data-testid="review-acknowledge"
            onClick={() => void resolveItem(item.id, "acknowledged", notes)}
            title="Mark as reviewed — rule edits remain a separate, deliberate act"
            style={buttonStyle}
          >
            Acknowledge
          </button>
          <button
            type="button"
            data-testid="review-dismiss"
            onClick={() => void resolveItem(item.id, "dismissed", notes)}
            title="Dismiss as not relevant (noted)"
            style={ghostButton}
          >
            Dismiss
          </button>
        </div>
      ) : (
        item.notes !== null && (
          <p data-testid="review-resolved-notes" style={{ margin: 0, color: "#8e8eb8", fontSize: "0.82rem" }}>
            notes: {item.notes}
          </p>
        )
      )}
    </li>
  );
}

export function CodesMonitorPage() {
  const sources = useCodesMonitorStore((s) => s.sources);
  const sourcesStatus = useCodesMonitorStore((s) => s.sourcesStatus);
  const sourcesError = useCodesMonitorStore((s) => s.sourcesError);
  const reviewItems = useCodesMonitorStore((s) => s.reviewItems);
  const reviewStatus = useCodesMonitorStore((s) => s.reviewStatus);
  const reviewError = useCodesMonitorStore((s) => s.reviewError);
  const run = useCodesMonitorStore((s) => s.run);
  const mutationError = useCodesMonitorStore((s) => s.mutationError);
  const loadSources = useCodesMonitorStore((s) => s.loadSources);
  const loadReviewItems = useCodesMonitorStore((s) => s.loadReviewItems);
  const addSource = useCodesMonitorStore((s) => s.addSource);
  const checkNow = useCodesMonitorStore((s) => s.checkNow);

  const [jurisdiction, setJurisdiction] = useState("");
  const [url, setUrl] = useState("");

  useEffect(() => {
    if (sourcesStatus === "idle") void loadSources();
  }, [sourcesStatus, loadSources]);
  useEffect(() => {
    if (reviewStatus === "idle") void loadReviewItems();
  }, [reviewStatus, loadReviewItems]);

  const onAdd = async (event: FormEvent) => {
    event.preventDefault();
    const j = jurisdiction.trim();
    const u = url.trim();
    if (!j || !u) return;
    if (await addSource(j, u)) {
      setJurisdiction("");
      setUrl("");
    }
  };

  const running = run.phase === "running";
  const sourceById = new Map(sources.map((s) => [s.id, s]));

  // Open items first, then by detection time (newest first) — stable view.
  const orderedItems = [...reviewItems].sort((a, b) => {
    const openDelta = Number(b.status === "open") - Number(a.status === "open");
    return openDelta !== 0 ? openDelta : b.detected_ms - a.detected_ms;
  });
  const openCount = reviewItems.filter((i) => i.status === "open").length;

  return (
    <main
      data-testid="codes-monitor-page"
      style={{
        fontFamily: "sans-serif",
        padding: "2rem",
        maxWidth: 860,
        margin: "0 auto",
        background: "#1a1a2e",
        color: "#d8d8e8",
        minHeight: "100vh",
        boxSizing: "border-box",
      }}
    >
      <nav style={{ marginBottom: "1rem" }}>
        <a href="/" style={{ color: "#8e8eb8" }}>
          ← Projects
        </a>
      </nav>
      <h1 style={{ marginTop: 0, fontSize: "1.4rem" }}>Codes monitor</h1>
      <p
        data-testid="monitor-posture"
        style={{
          background: "#21213a",
          borderLeft: "3px solid #e5c07b",
          borderRadius: 6,
          padding: "0.7rem 0.9rem",
          fontSize: "0.88rem",
        }}
      >
        <strong>istari never auto-updates rules.</strong> The monitor only watches allowlisted
        jurisdiction pages for changes (content-hash diff). Every detected change lands in the
        review queue below for human review — encoding a rule change stays a deliberate,
        attributed act by an engineer.
      </p>

      {mutationError && (
        <p data-testid="monitor-mutation-error" style={{ color: "#e06c75" }}>
          {mutationError}
        </p>
      )}

      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          gap: "1rem",
          flexWrap: "wrap",
        }}
      >
        <h2 style={sectionTitle}>Monitored sources</h2>
        <button
          type="button"
          data-testid="check-now-button"
          onClick={() => void checkNow()}
          disabled={running || sources.length === 0}
          title="Run one change-detection check across all monitored sources (content-hash diff)"
          style={
            running || sources.length === 0
              ? { ...buttonStyle, background: "#2a2a44", cursor: running ? "wait" : "not-allowed" }
              : buttonStyle
          }
        >
          {running ? `Checking… ${run.progress}%` : "Check now (all sources)"}
        </button>
      </div>
      {run.phase === "failed" && (
        <p data-testid="check-error" style={{ margin: "0 0 0.6rem", color: "#e06c75", fontSize: "0.82rem" }}>
          Check failed: {run.message}
        </p>
      )}
      <form
        onSubmit={(e) => void onAdd(e)}
        style={{ display: "flex", gap: "0.5rem", marginBottom: "0.8rem", flexWrap: "wrap" }}
      >
        <input
          data-testid="source-jurisdiction-input"
          value={jurisdiction}
          onChange={(e) => setJurisdiction(e.target.value)}
          placeholder="Jurisdiction id (e.g. santa-clara-county)"
          style={{ ...inputStyle, flex: 1, minWidth: 200 }}
        />
        <input
          data-testid="source-url-input"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="Allowlisted page URL"
          style={{ ...inputStyle, flex: 2, minWidth: 240 }}
        />
        <button
          type="submit"
          data-testid="source-add-button"
          disabled={jurisdiction.trim() === "" || url.trim() === ""}
          style={buttonStyle}
        >
          Add source
        </button>
      </form>

      {sourcesStatus === "loading" && <p>Loading sources…</p>}
      {sourcesStatus === "error" && (
        <div data-testid="sources-error" style={{ color: "#e06c75" }}>
          <p>Could not load sources: {sourcesError}</p>
          <button type="button" onClick={() => void loadSources()} style={ghostButton}>
            Retry
          </button>
        </div>
      )}
      {sourcesStatus === "ready" && sources.length === 0 && (
        <p data-testid="sources-empty" style={{ color: "#8e8eb8" }}>
          No sources monitored yet — add an allowlisted jurisdiction page above.
        </p>
      )}
      <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
        {sources.map((source) => (
          <li key={source.id} data-testid="source-row" style={cardStyle}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: "1rem",
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <div style={{ minWidth: 0 }}>
                <strong>{source.jurisdiction}</strong>
                <div
                  style={{
                    color: "#8e8eb8",
                    fontSize: "0.8rem",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    maxWidth: 420,
                  }}
                >
                  {source.url}
                </div>
                <div data-testid="source-last-checked" style={{ color: "#8e8eb8", fontSize: "0.8rem" }}>
                  last checked: {lastChecked(source.last_checked_ms)}
                </div>
              </div>
            </div>
          </li>
        ))}
      </ul>

      <h2 style={sectionTitle}>
        Review queue{openCount > 0 && ` — ${openCount} open`}
      </h2>
      {reviewStatus === "loading" && <p>Loading review queue…</p>}
      {reviewStatus === "error" && (
        <div data-testid="review-error" style={{ color: "#e06c75" }}>
          <p>Could not load the review queue: {reviewError}</p>
          <button type="button" onClick={() => void loadReviewItems()} style={ghostButton}>
            Retry
          </button>
        </div>
      )}
      {reviewStatus === "ready" && reviewItems.length === 0 && (
        <p data-testid="review-empty" style={{ color: "#8e8eb8" }}>
          No detected changes awaiting review.
        </p>
      )}
      <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
        {orderedItems.map((item) => (
          <ReviewItemCard key={item.id} item={item} source={sourceById.get(item.source_id)} />
        ))}
      </ul>
    </main>
  );
}
